import React, { useState } from 'react';
import { ProjectInfo } from '../../types';

interface DocumentFile {
    id: string;
    name: string;
    type: string;
    size: number;
    uploadDate: string;
    uploadedBy: string;
    version: number;
    status: 'Vigente' | 'Histórico' | 'Papelera';
    projectId?: string; // ID del proyecto (opcional para docs globales)
    planningModel?: 'Maestra' | 'Intermedia' | 'Semanal' | 'N/A';
    url?: string;
}

interface DocumentManagerProps {
    projects: (ProjectInfo & { id: string })[];
}

const DocumentManager: React.FC<DocumentManagerProps> = ({ projects }) => {
    // Definimos el estado de los archivos simulados usando los proyectos reales si existen
    const defaultProjectId = projects.length > 0 ? projects[0].id : 'proj-test';
    
    const [files, setFiles] = useState<DocumentFile[]>([
        {
            id: 'doc-1',
            name: 'Cronograma_Maestro_V2.mpp',
            type: 'application/vnd.ms-project',
            size: 2500000,
            uploadDate: '2026-03-25T10:30:00Z',
            uploadedBy: 'Administrador',
            version: 2,
            status: 'Vigente',
            projectId: defaultProjectId,
            planningModel: 'Maestra'
        },
        {
            id: 'doc-2',
            name: 'Cronograma_Maestro_V1.mpp',
            type: 'application/vnd.ms-project',
            size: 2450000,
            uploadDate: '2026-03-20T09:15:00Z',
            uploadedBy: 'Jefe de Terreno',
            version: 1,
            status: 'Histórico',
            projectId: defaultProjectId,
            planningModel: 'Maestra'
        },
        {
            id: 'doc-3',
            name: 'Manual_Calidad_Corporativo.pdf',
            type: 'application/pdf',
            size: 8400000,
            uploadDate: '2026-03-26T14:20:00Z',
            uploadedBy: 'Gerencia',
            version: 1,
            status: 'Vigente',
            planningModel: 'N/A'
        }
    ]);

    const [activeTab, setActiveTab] = useState<'Vigente' | 'Histórico' | 'Papelera'>('Vigente');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterProject, setFilterProject] = useState<string>('ALL');
    const [filterModule, setFilterModule] = useState<string>('ALL');

    // Estado del Modal de Subida
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [uploadConfig, setUploadConfig] = useState<{
        projectId: string,
        planningModel: 'Maestra' | 'Intermedia' | 'Semanal' | 'N/A'
    }>({
        projectId: '',
        planningModel: 'N/A'
    });

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const filesArray = Array.from(e.target.files) as File[];
            setSelectedFiles(filesArray);
            // Si el archivo parece un Project, preseleccionamos modelos
            const firstFile = filesArray[0];
            if (firstFile.name.endsWith('.mpp')) {
                setUploadConfig({ ...uploadConfig, planningModel: 'Maestra' });
            }
            setIsUploadModalOpen(true);
        }
        // Reseteamos el input para permitir seleccionar el mismo archivo de nuevo
        e.target.value = '';
    };

    const confirmUpload = () => {
        if (!selectedFiles) return;

        const date = new Date().toISOString();
        const newFiles: DocumentFile[] = [];
        let currentFiles = [...files];

        selectedFiles.forEach((file: File) => {
            const isGantt = uploadConfig.planningModel !== 'N/A' && uploadConfig.projectId !== '';
            let existingFiles: DocumentFile[] = [];

            // Obtenemos las versiones existentes para calcular la nueva
            if (isGantt) {
                // Versionar por Proyecto y Modelo
                existingFiles = currentFiles.filter(f => 
                    f.projectId === uploadConfig.projectId && 
                    f.planningModel === uploadConfig.planningModel && 
                    f.status !== 'Papelera'
                );
            } else {
                // Versionar por nombre para documentos globales
                existingFiles = currentFiles.filter(f => f.name === file.name && f.status !== 'Papelera');
            }

            const newVersion = existingFiles.length > 0 ? Math.max(...existingFiles.map(f => f.version)) + 1 : 1;

            newFiles.push({
                id: `doc-${Date.now()}-${Math.random()}`,
                name: file.name,
                type: file.type || getExtensionType(file.name),
                size: file.size,
                uploadDate: date,
                uploadedBy: 'Administrador Global', // Mocked user
                version: newVersion,
                status: 'Vigente',
                projectId: uploadConfig.projectId || undefined,
                planningModel: uploadConfig.planningModel
            });

            // Archivar versiones anteriores
            if (isGantt) {
                 currentFiles = currentFiles.map(f => 
                    (f.projectId === uploadConfig.projectId && f.planningModel === uploadConfig.planningModel && f.status === 'Vigente') 
                        ? { ...f, status: 'Histórico' as const } 
                        : f
                );
            } else {
                 currentFiles = currentFiles.map(f => 
                    (f.name === file.name && f.status === 'Vigente') 
                        ? { ...f, status: 'Histórico' as const } 
                        : f
                );
            }
        });

        setFiles([...newFiles, ...currentFiles]);
        setIsUploadModalOpen(false);
        setSelectedFiles(null);
        setUploadConfig({ projectId: '', planningModel: 'N/A' });
    };

    const getExtensionType = (filename: string) => {
        const ext = filename.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return 'application/pdf';
        if (ext === 'mpp') return 'application/vnd.ms-project';
        if (['jpg', 'jpeg', 'png'].includes(ext || '')) return 'image/jpeg';
        return 'application/octet-stream';
    };

    const handleSetAsActive = (file: DocumentFile) => {
        if(confirm(`⚠️ AUTORIDAD ADMINISTRATIVA:\n\n¿Forzar la versión ${file.version} de "${file.name}" como la VIGENTE?\n\nLa Gantt del proyecto se actualizará inmediatamente para reflejar esta versión.`)) {
            setFiles(files.map(f => {
                // Desmarcar actual vigente (Mismo Proyecto y Mismo Modelo)
                if (file.projectId && file.planningModel !== 'N/A') {
                    if (f.projectId === file.projectId && f.planningModel === file.planningModel && f.status === 'Vigente') {
                        return { ...f, status: 'Histórico' as const };
                    }
                } else if (!file.projectId) {
                    if (f.name === file.name && f.status === 'Vigente') {
                         return { ...f, status: 'Histórico' as const };
                    }
                }
                
                // Activar seleccionado
                if (f.id === file.id) {
                    return { ...f, status: 'Vigente' as const };
                }
                return f;
            }));
        }
    };

    const handleMoveToTrash = (id: string, name: string) => {
        if (confirm(`¿Mover "${name}" a la papelera?`)) {
            setFiles(files.map(f => f.id === id ? { ...f, status: 'Papelera' } : f));
        }
    };

    const handleRestore = (id: string, name: string) => {
        if (confirm(`¿Restaurar "${name}"?`)) {
            setFiles(files.map(f => f.id === id ? { ...f, status: 'Histórico' } : f));
        }
    };

    const handlePermanentDelete = (id: string, name: string) => {
        if (confirm(`⚠️ ATENCIÓN: Esta acción es irreversible.\n¿Eliminar definitivamente "${name}" del servidor?`)) {
            setFiles(files.filter(f => f.id !== id));
        }
    };

    const formatBytes = (bytes: number, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024, dm = decimals < 0 ? 0 : decimals, sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const getProjectName = (projectId?: string) => {
        if (!projectId) return 'Sin Obra asignada';
        const proj = projects.find(p => p.id === projectId);
        return proj ? proj.name : 'Proyecto Desconocido';
    };

    // Filtramos
    const filteredFiles = files.filter(f => {
        const matchStatus = f.status === activeTab;
        const matchSearch = f.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchProject = filterProject === 'ALL' || f.projectId === filterProject || (filterProject === 'GLOBAL' && !f.projectId);
        const matchModule = filterModule === 'ALL' || f.planningModel === filterModule || (filterModule === 'N/A' && (!f.planningModel || f.planningModel === 'N/A'));
        return matchStatus && matchSearch && matchProject && matchModule;
    }).sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());

    const storageUsed = files.reduce((acc, f) => acc + f.size, 0);

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-[#0d131b] p-4 md:p-8 overflow-hidden relative w-full">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3 md:gap-0 mb-3 md:mb-4 flex-shrink-0">
                <div>
                    <h1 className="text-lg md:text-xl font-black text-slate-800 dark:text-white mb-1 tracking-tight">Gestor Documental</h1>
                    <p className="text-slate-500 text-xs md:text-sm font-medium">Administración centralizada de cartas Gantt y documentos relacionados al control de obras.</p>
                </div>
            </div>

            <div className="flex flex-col flex-1 overflow-hidden bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm relative">
                
                {/* Header / Tabs */}
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center px-4 md:px-6 pt-3 md:pt-4 pb-3 border-b border-slate-200 dark:border-slate-800 gap-3">
                    <div className="flex gap-2 overflow-x-auto w-full xl:w-auto pb-1 xl:pb-0 scrollbar-hidden">
                        {(['Vigente', 'Histórico', 'Papelera'] as const).map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-xl font-bold text-xs transition-all focus:outline-none flex items-center gap-1.5 whitespace-nowrap ${activeTab === tab 
                                    ? tab === 'Papelera' ? 'bg-red-50 text-red-600 dark:bg-red-900/20' : 'bg-primary text-white shadow-md'
                                    : 'bg-transparent text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                            >
                                <span className="material-symbols-outlined text-[16px]">
                                    {tab === 'Vigente' ? 'check_circle' : tab === 'Histórico' ? 'history' : 'delete'}
                                </span>
                                {tab}
                                <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded-full ${activeTab === tab ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>
                                    {files.filter(f => f.status === tab).length}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-2 w-full xl:w-auto">
                        <div className="flex items-center rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-full md:w-auto">
                            <span className="material-symbols-outlined text-slate-400 text-[16px] pl-2">filter_alt</span>
                            <select 
                                value={filterProject}
                                onChange={(e) => setFilterProject(e.target.value)}
                                className="bg-transparent text-xs w-full md:w-auto text-slate-700 dark:text-slate-300 font-bold px-2 py-1.5 outline-none appearance-none cursor-pointer truncate max-w-[200px]"
                            >
                                <option value="ALL">Todo (Global + Proyectos)</option>
                                <option value="GLOBAL">Solo Archivos Globales</option>
                                {projects.map(p => (
                                    <option key={p.id} value={p.id}>Proyecto: {p.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-full md:w-auto">
                            <span className="material-symbols-outlined text-slate-400 text-[16px] pl-2">schema</span>
                            <select 
                                value={filterModule}
                                onChange={(e) => setFilterModule(e.target.value)}
                                className="bg-transparent text-xs w-full md:w-auto text-slate-700 dark:text-slate-300 font-bold px-2 py-1.5 outline-none appearance-none cursor-pointer truncate max-w-[150px]"
                            >
                                <option value="ALL">Todo (Gantt y Otros)</option>
                                <option value="Maestra">Gantt Maestra</option>
                                <option value="Intermedia">Gantt Intermedia</option>
                                <option value="Semanal">Gantt Semanal</option>
                                <option value="N/A">Otros Documentos</option>
                            </select>
                        </div>
                        <div className="flex flex-1 md:w-40 items-center rounded-xl bg-slate-50 dark:bg-slate-800 px-2 py-1.5 border border-slate-200 dark:border-slate-700 w-full md:w-auto">
                            <span className="material-symbols-outlined text-slate-400 text-[16px]">search</span>
                            <input 
                                className="ml-1.5 w-full bg-transparent text-xs text-slate-900 placeholder-slate-400 focus:outline-none dark:text-white" 
                                placeholder="Buscar archivo..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="relative w-full md:w-auto mt-2 md:mt-0 flex justify-center">
                            <input 
                                type="file" 
                                multiple 
                                onChange={handleFileSelect} 
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                                title="Subir nuevos documentos"
                            />
                            <button className="justify-center bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-lg shadow-primary/20 pointer-events-none min-w-[120px]">
                                <span className="material-symbols-outlined text-[16px]">cloud_upload</span>
                                Subir
                            </button>
                        </div>
                    </div>
                </div>

                {/* File List */}
                <div className="flex-1 overflow-y-auto">
                    {filteredFiles.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full p-12 text-center text-slate-400">
                            <span className="material-symbols-outlined text-6xl mb-4 text-slate-300 dark:text-slate-700">folder_open</span>
                            <h3 className="text-lg font-bold text-slate-500 mb-1">No se encontraron archivos</h3>
                            <p className="text-sm max-w-sm">
                                Modifica los filtros o sube documentos PDF, imágenes o cronogramas MPP usando el botón de 'Subir'.
                            </p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 bg-slate-50 dark:bg-[#1e2c3c] z-10 border-b border-slate-200 dark:border-slate-800">
                                <tr>
                                    <th className="py-4 pl-4 md:pl-6 text-xs font-black text-slate-500 tracking-widest">Archivos</th>
                                    <th className="py-4 pr-4 md:pr-6 text-xs font-black text-slate-500 tracking-widest text-left">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filteredFiles.map(file => (
                                    <tr key={file.id} className="hover:bg-slate-50 dark:hover:bg-[#1e2a3b] transition-colors group">
                                        <td className="p-4 pl-4 md:pl-6 align-top pt-4">
                                            <p className="font-bold text-[11px] text-slate-800 dark:text-white mb-1">
                                                {file.name}
                                            </p>
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${
                                                    activeTab === 'Vigente' ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-blue-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                                }`}>
                                                    VERSIÓN {file.version}
                                                </span>
                                                <span className="text-[10px] text-slate-400 font-medium">{formatBytes(file.size)}</span>
                                            </div>
                                            
                                            <div className="flex flex-col gap-1 mt-3">
                                                 <div className="flex flex-wrap md:flex-nowrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                                                     <div className="flex items-center gap-1.5">
                                                         <span className="material-symbols-outlined text-[14px] shrink-0">apartment</span>
                                                         <span className="font-bold whitespace-nowrap">{getProjectName(file.projectId)}</span>
                                                     </div>
                                                     {file.planningModel && file.planningModel !== 'N/A' && (
                                                         <span className="inline-flex items-center text-[9px] font-bold text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 rounded-md shrink-0 ml-1">
                                                             Gantt {file.planningModel}
                                                         </span>
                                                     )}
                                                 </div>
                                                 <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                                     <span className="material-symbols-outlined text-[14px]">calendar_today</span>
                                                     {new Date(file.uploadDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                 </div>
                                                 <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                                     <span className="material-symbols-outlined text-[14px]">person</span>
                                                     {file.uploadedBy}
                                                 </div>
                                            </div>
                                        </td>
                                        <td className="p-2 md:p-4 pr-4 md:pr-6 text-right align-top pt-4 whitespace-nowrap">
                                            <div className="flex justify-end gap-1 transition-opacity">
                                                {activeTab === 'Histórico' && file.projectId && file.planningModel !== 'N/A' && (
                                                    <button 
                                                        onClick={() => handleSetAsActive(file)}
                                                        className="flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 transition-colors px-3 py-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 mr-2 border border-emerald-200 dark:border-emerald-800 shadow-sm"
                                                        title="Forzar esta versión en la Carta Gantt del proyecto"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                        Fijar como Vigente
                                                    </button>
                                                )}

                                                {activeTab !== 'Papelera' ? (
                                                    <>
                                                        <button className="text-slate-400 hover:text-primary transition-colors p-2 rounded-lg hover:bg-primary/10">
                                                            <span className="material-symbols-outlined text-[20px]">download</span>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleMoveToTrash(file.id, file.name)}
                                                            className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">delete</span>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button 
                                                            onClick={() => handleRestore(file.id, file.name)}
                                                            className="text-slate-400 hover:text-green-500 transition-colors p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">restore_from_trash</span>
                                                        </button>
                                                        <button 
                                                            onClick={() => handlePermanentDelete(file.id, file.name)}
                                                            className="text-slate-400 hover:text-red-600 transition-colors p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">delete_forever</span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Upload Configuration Modal */}
            {isUploadModalOpen && selectedFiles && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-[#1a2634] rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden animate-scale-up border border-slate-200 dark:border-slate-700">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1e2c3c]">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Clasificación de Archivo</h3>
                            <p className="text-sm text-slate-500 mt-1">Has seleccionado {selectedFiles.length} archivo(s). ¿Dónde quieres vincularlo?</p>
                        </div>
                        <div className="p-6 space-y-6">
                            
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50">
                                <p className="text-sm font-bold text-blue-800 dark:text-blue-300 line-clamp-2">
                                    <span className="material-symbols-outlined align-bottom text-[18px] mr-1">attach_file</span>
                                    {selectedFiles[0].name} {selectedFiles.length > 1 && `y ${selectedFiles.length - 1} más`}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-bold text-slate-500 uppercase">Vincular a Proyecto</label>
                                    <select
                                        value={uploadConfig.projectId}
                                        onChange={e => setUploadConfig({ ...uploadConfig, projectId: e.target.value })}
                                        className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium text-slate-800 dark:text-white"
                                    >
                                        <option value="">Documento Global (Sin Proyecto)</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {uploadConfig.projectId && (
                                    <div className="flex flex-col gap-1 animate-fade-in">
                                        <label className="text-xs font-bold text-slate-500 uppercase">Modelo de Planificación (Gantt)</label>
                                        <select
                                            value={uploadConfig.planningModel}
                                            onChange={e => setUploadConfig({ ...uploadConfig, planningModel: e.target.value as any })}
                                            className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all font-medium text-slate-800 dark:text-white"
                                        >
                                            <option value="N/A">No es Carta Gantt (Documento General)</option>
                                            <option value="Maestra">Planificación Maestra</option>
                                            <option value="Intermedia">Planificación Intermedia</option>
                                            <option value="Semanal">Planificación Semanal</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-[#1e2c3c]">
                            <button
                                onClick={() => { setIsUploadModalOpen(false); setSelectedFiles(null); }}
                                className="px-5 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmUpload}
                                className="px-6 py-2 bg-primary text-white rounded-xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">cloud_upload</span>
                                Confirmar y Subir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentManager;
